import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Plus, Search, UserMinus, UserCheck, X } from "lucide-react";

const API = "https://api.komorotel.com/api";

const euros = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} €`;
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "jamais";

export default function OrgMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [drawer, setDrawer]   = useState(false);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/organization/members`, { withCredentials: true })
      .then(r => setMembers(r.data))
      .catch(() => toast.error("Impossible de charger les membres."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const basculer = async (m) => {
    const suite = m.status === "active" ? "suspended" : "active";
    try {
      const r = await axios.patch(
        `${API}/organization/members/${m._id}`, { status: suite }, { withCredentials: true }
      );
      toast.success(suite === "active" ? "Membre réactivé." : "Membre suspendu.");
      if (r.data.upcomingReservations > 0) {
        toast(`${r.data.upcomingReservations} séjour${r.data.upcomingReservations > 1 ? "s" : ""} à venir restent confirmés.`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur.");
    }
  };

  const filtres = members.filter(m => {
    const t = search.toLowerCase().trim();
    if (!t) return true;
    return [m.firstName, m.lastName, m.email].filter(Boolean).join(" ").toLowerCase().includes(t);
  });

  const nom = (m) => [m.firstName, m.lastName].filter(Boolean).join(" ") || m.email;

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-8 sm:py-10 max-w-[980px]">

      <header className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-[23px] sm:text-[27px] leading-tight font-semibold tracking-[-0.01em] text-[var(--org-ink)]">
            Membres
          </h1>
          <p className="text-[13.5px] sm:text-[13.5px] text-[var(--org-slate)] mt-1 max-w-xl">
            Figurer ici ne donne aucun droit de réserver. L'autorisation vient
            de la mission, ou de votre validation.
          </p>
        </div>
        <button
          onClick={() => setDrawer(true)}
          className="flex items-center gap-1.5 px-3.5 py-[7px] rounded-lg text-[13.5px] font-semibold text-white transition hover:opacity-90 shrink-0"
          style={{ background: "var(--org-forest)" }}
        >
          <Plus size={16} strokeWidth={2} /> Ajouter
        </button>
      </header>

      {members.length > 0 && (
      <div className="relative max-w-xs mb-6">
        <Search size={15} strokeWidth={1.75} className="absolute left-0 top-1/2 -translate-y-1/2 text-[var(--org-slate)]" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher"
          className="w-full pl-6 pr-3 py-2 text-[15px] bg-transparent outline-none text-[var(--org-ink)] placeholder:text-[var(--org-slate)]"
          style={{ borderBottom: "1px solid var(--org-line)" }}
        />
      </div>
      )}

      {/* En-tête de colonnes, écran large seulement */}
      {!loading && filtres.length > 0 && (
        <div
          className="hidden sm:flex items-baseline gap-6 pb-2 text-[12.5px] text-[var(--org-slate)]"
          style={{ borderBottom: "1px solid var(--org-line)" }}
        >
          <span className="flex-1">Membre</span>
          <span className="w-16 text-right">Séjours</span>
          <span className="w-16 text-right">Nuits</span>
          <span className="w-24 text-right">Total</span>
          <span className="w-32">Dernier séjour</span>
          <span className="w-8" />
        </div>
      )}

      <div>
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="py-5" style={{ borderBottom: "1px solid var(--org-line)" }}>
              <div className="h-4 w-44 bg-gray-200/60 rounded animate-pulse" />
            </div>
          ))
        ) : filtres.length === 0 ? (
          <div className="max-w-xl">
            {members.length === 0 ? (
              <>
                <p className="text-[14px] text-[var(--org-ink)]">
                  Personne n'est encore rattaché à votre organisation.
                </p>
                <p className="text-[13.5px] text-[var(--org-slate)] mt-1.5 leading-relaxed">
                  Ajoutez les personnes que vous envoyez en mission. Un nom et un
                  email suffisent : elles n'ont pas de compte à créer et ne reçoivent
                  qu'une information, sans action à faire de leur côté.
                </p>
                <button
                  onClick={() => setDrawer(true)}
                  className="mt-5 px-3.5 py-[7px] rounded-lg text-[13.5px] font-semibold text-white transition hover:opacity-90"
                  style={{ background: "var(--org-forest)" }}
                >
                  Ajouter le premier membre
                </button>
              </>
            ) : (
              <p className="text-[15px] text-[var(--org-slate)]">
                Aucun membre ne correspond à « {search} ».
              </p>
            )}
          </div>
        ) : (
          filtres.map(m => (
            <div
              key={m._id}
              className="py-4 sm:flex sm:items-baseline sm:gap-6"
              style={{ borderBottom: "1px solid var(--org-line)" }}
            >
              <div className="flex-1 min-w-0">
                <p className="text-[16px] sm:text-[14px] text-[var(--org-ink)] truncate">
                  {nom(m)}
                  {m.status === "suspended" && (
                    <span className="ml-2 text-[12px] text-[var(--org-slate)]">
                      {m.declinedAt ? "s'est retiré" : "suspendu"}
                    </span>
                  )}
                </p>
                <p className="text-[13px] text-[var(--org-slate)] truncate mt-0.5">{m.email}</p>
              </div>

              {/* Téléphone : les chiffres en une phrase, pas en colonnes */}
              <p className="sm:hidden text-[13px] text-[var(--org-slate)] mt-2">
                <span className="org-num text-[var(--org-ink)]">{m.stats?.bookings ?? 0}</span> séjour
                {(m.stats?.bookings ?? 0) > 1 ? "s" : ""},{" "}
                <span className="org-num text-[var(--org-ink)]">{m.stats?.nights ?? 0}</span> nuit
                {(m.stats?.nights ?? 0) > 1 ? "s" : ""},{" "}
                <span className="org-num text-[var(--org-ink)]">{euros(m.stats?.total)}</span>
                {m.stats?.lastStay ? `, dernier séjour le ${fmtDate(m.stats.lastStay)}` : ""}
              </p>

              <p className="hidden sm:block org-num w-16 text-right text-[14px] text-[var(--org-ink)]">
                {m.stats?.bookings ?? 0}
              </p>
              <p className="hidden sm:block org-num w-16 text-right text-[14px] text-[var(--org-ink)]">
                {m.stats?.nights ?? 0}
              </p>
              <p className="hidden sm:block org-num w-24 text-right text-[15px] font-medium text-[var(--org-ink)]">
                {euros(m.stats?.total)}
              </p>
              <p className="hidden sm:block w-32 text-[13px] text-[var(--org-slate)]">
                {fmtDate(m.stats?.lastStay)}
              </p>

              <div className="sm:w-8 mt-3 sm:mt-0 shrink-0">
                {/* Quelqu'un qui s'est retiré lui-même n'est pas réinscrit de force. */}
                {!m.declinedAt && (
                  <button
                    onClick={() => basculer(m)}
                    className="flex items-center gap-1.5 sm:gap-0 text-[13px] text-[var(--org-slate)] hover:text-[var(--org-ink)] transition"
                    title={m.status === "active" ? "Suspendre" : "Réactiver"}
                  >
                    {m.status === "active"
                      ? <><UserMinus size={16} strokeWidth={1.75} /> <span className="sm:hidden">Suspendre</span></>
                      : <><UserCheck size={16} strokeWidth={1.75} /> <span className="sm:hidden">Réactiver</span></>}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {drawer && <AjoutMembre onClose={() => setDrawer(false)} onDone={() => { setDrawer(false); load(); }} />}
    </div>
  );
}

function AjoutMembre({ onClose, onDone }) {
  const [form, setForm]       = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const envoyer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/organization/members`, form, { withCredentials: true });
      toast.success(r.status === 201 ? "Membre ajouté." : "Ce membre était déjà dans la liste.");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur.");
    } finally {
      setLoading(false);
    }
  };

  const champ = (label, k, type = "text", requis = false) => (
    <label key={k} className="block">
      <span className="text-[13px] text-[var(--org-slate)]">{label}</span>
      <input
        type={type}
        value={form[k]}
        required={requis}
        onChange={e => setForm(s => ({ ...s, [k]: e.target.value }))}
        className="w-full mt-1 py-2 text-[15px] bg-transparent outline-none text-[var(--org-ink)] focus:border-[var(--org-forest)] transition"
        style={{ borderBottom: "1px solid var(--org-line)" }}
      />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/25" onClick={onClose}>
      <div
        className="w-full sm:max-w-sm h-full overflow-y-auto px-5 sm:px-7 py-6"
        style={{ background: "var(--org-paper)" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="text-[17px] font-semibold text-[var(--org-ink)]">Ajouter un membre</h2>
          <button onClick={onClose} className="text-[var(--org-slate)] hover:text-[var(--org-ink)] transition shrink-0">
            <X size={20} strokeWidth={1.75} />
          </button>
        </div>
        <p className="text-[13.5px] text-[var(--org-slate)] mb-7">
          Aucun compte à créer. La personne reçoit une information par mail,
          sans action à faire de son côté.
        </p>

        <form onSubmit={envoyer} className="flex flex-col gap-5">
          {champ("Prénom", "firstName")}
          {champ("Nom", "lastName")}
          {champ("Email", "email", "email", true)}
          {champ("Téléphone", "phone", "tel")}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 mt-2 rounded-lg text-[15px] font-medium text-white transition hover:opacity-90 disabled:opacity-50"
            style={{ background: "var(--org-forest)" }}
          >
            {loading ? "Ajout..." : "Ajouter le membre"}
          </button>
        </form>
      </div>
    </div>
  );
}
